import React, { useState } from 'react';
import { Card, CardContent, Typography, Grid, Switch, Button, Chip } from '@mui/material';
import { Building, ShieldCheck, Cpu, HardDrive, Database, Settings } from 'lucide-react';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  plan: string;
  modules: string[];
}

const INITIAL_TENANTS: Tenant[] = [
  { id: '1', name: 'St. Jude Special School', slug: 'stjude', plan: 'ENTERPRISE_SCHOOL', modules: ['student', 'teacher', 'therapist', 'aac', 'ai'] },
  { id: '2', name: 'Sunshine Therapy Centre', slug: 'sunshine', plan: 'PRO_CLINIC', modules: ['student', 'therapist', 'aac'] },
  { id: '3', name: 'District Rehab Department', slug: 'rehab', plan: 'GOVT_REHAB', modules: ['student', 'teacher', 'therapist', 'hospital', 'csr', 'aac', 'ai'] }
];

const ALL_MODULES = [
  { key: 'student', label: 'Student Module' },
  { key: 'teacher', label: 'Teacher Module' },
  { key: 'therapist', label: 'Therapist Module' },
  { key: 'speech-therapy', label: 'Speech Therapy Module' },
  { key: 'aac', label: 'AAC Module' },
  { key: 'autism', label: 'Autism Module' },
  { key: 'hospital', label: 'Hospital Module' },
  { key: 'school-erp', label: 'School ERP Module' },
  { key: 'ai', label: 'AI Module' }
];

export const SuperAdminDashboard: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>(INITIAL_TENANTS);

  const toggleTenantModule = (tenantId: string, moduleKey: string) => {
    setTenants((prev) =>
      prev.map((tenant) => {
        if (tenant.id !== tenantId) return tenant;
        const exists = tenant.modules.includes(moduleKey);
        const updatedModules = exists
          ? tenant.modules.filter((m) => m !== moduleKey)
          : [...tenant.modules, moduleKey];
        return { ...tenant, modules: updatedModules };
      })
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Typography variant="h4" className="font-extrabold text-slate-800">
            WHM / cPanel Super Admin Control Plane
          </Typography>
          <Typography variant="body2" className="text-slate-500">
            Single Codebase Dynamic Module Manager & Tenant Control
          </Typography>
        </div>
        <Chip label="System Health: 100% Operational" color="success" className="font-bold" />
      </div>

      {/* Control Metrics Grid */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={3}>
          <Card className="border border-slate-200">
            <CardContent className="flex items-center gap-4">
              <Building className="w-10 h-10 text-blue-600" />
              <div>
                <Typography variant="subtitle2" className="text-slate-500">Total Organizations</Typography>
                <Typography variant="h4" className="font-bold">1,420</Typography>
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card className="border border-slate-200">
            <CardContent className="flex items-center gap-4">
              <Cpu className="w-10 h-10 text-emerald-600" />
              <div>
                <Typography variant="subtitle2" className="text-slate-500">CPU Load (AlmaLinux)</Typography>
                <Typography variant="h4" className="font-bold">12%</Typography>
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card className="border border-slate-200">
            <CardContent className="flex items-center gap-4">
              <HardDrive className="w-10 h-10 text-purple-600" />
              <div>
                <Typography variant="subtitle2" className="text-slate-500">Redis Memory</Typography>
                <Typography variant="h4" className="font-bold">142 MB</Typography>
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card className="border border-slate-200">
            <CardContent className="flex items-center gap-4">
              <Database className="w-10 h-10 text-amber-600" />
              <div>
                <Typography variant="subtitle2" className="text-slate-500">MariaDB Queries / sec</Typography>
                <Typography variant="h4" className="font-bold">450 / s</Typography>
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tenant Dynamic Module Management Table */}
      <Card className="border border-slate-200">
        <CardContent>
          <Typography variant="h6" className="font-bold text-slate-800 mb-4">
            Dynamic Module Authorization Matrix (Real-Time Enable / Disable)
          </Typography>

          {tenants.map((tenant) => (
            <div key={tenant.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200 mb-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <Typography variant="subtitle1" className="font-bold text-slate-900">
                    {tenant.name} <span className="text-xs text-slate-400 font-mono">({tenant.slug}.abilityos.bugfreesolution.com)</span>
                  </Typography>
                  <Chip label={tenant.plan} size="small" color="primary" className="mt-1" />
                </div>
                <Button size="small" variant="outlined" startIcon={<Settings className="w-4 h-4" />}>
                  Manage Tenant
                </Button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 pt-2 border-t border-slate-200">
                {ALL_MODULES.map((mod) => {
                  const isEnabled = tenant.modules.includes(mod.key);
                  return (
                    <div key={mod.key} className="flex items-center justify-between p-2 bg-white rounded border border-slate-200">
                      <span className="text-xs font-semibold text-slate-700">{mod.label}</span>
                      <Switch
                        size="small"
                        checked={isEnabled}
                        onChange={() => toggleTenantModule(tenant.id, mod.key)}
                        color="primary"
                        aria-label={`Toggle ${mod.label} for ${tenant.name}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};
